import React, { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

export default function ConfirmationPage() {
  const navigate = useNavigate();
  const location = useLocation();
  
  const confirmationData = location.state || {};
  const {
    registrationId,
    receiptUrl,
    email,
    emailSent,
    emailError,
    attendees = [],
    sessions = [],
    centerName,
    campName
  } = confirmationData;

  useEffect(() => {
    if (!registrationId) {
      navigate('/retreat');
    }
  }, [registrationId, navigate]);

  const handleDownloadInvoice = () => {
    if (receiptUrl) {
      window.open(receiptUrl, '_blank');
    }
  };

  const handleViewInvoice = () => {
    if (receiptUrl) {
      window.open(receiptUrl, '_blank');
    }
  };

  const handleReturnHome = () => {
    navigate('/retreat');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 via-emerald-50 to-teal-50 flex items-start justify-center p-3 pt-4">
      <div className="max-w-3xl w-full">
        <div className="bg-white/80 backdrop-blur-xl rounded-3xl shadow-2xl border border-white/60 overflow-hidden">
          {/* Success Header */}
          <div className="relative bg-gradient-to-br from-green-500 via-green-600 to-emerald-600 text-white p-5 text-center overflow-hidden">
            <div className="absolute top-0 right-0 w-24 h-24 bg-white/10 rounded-full -mr-12 -mt-12"></div>
            <div className="absolute bottom-0 left-0 w-20 h-20 bg-white/10 rounded-full -ml-8 -mb-8"></div>
            
            <div className="relative w-10 h-10 bg-white rounded-full flex items-center justify-center mx-auto mb-2 shadow-lg">
              <i className="fas fa-check text-2xl text-green-500"></i>
            </div>
            
            <h1 className="relative text-1xl font-extrabold mb-1 tracking-tight">Registration Successful!</h1>
            <p className="relative text-green-50 text-sm font-medium">Your retreat registration has been confirmed</p>
          </div>

          {/* Content */}
          <div className="p-5 space-y-4">
            {/* Registration Details */}
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-4 border border-blue-200">
              <div className="flex items-center gap-2 mb-3">
                <i className="fas fa-file-alt text-lg text-blue-600"></i>
                <h3 className="font-bold text-gray-800 text-base">Registration Details</h3>
              </div>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between items-center">
                  <span className="text-gray-600 font-medium hidden">Registration ID:</span>
                  <span className="font-bold text-gray-900 hidden">{registrationId || 'N/A'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600 font-medium">Center:</span>
                  <span className="font-semibold text-gray-800">{centerName || 'N/A'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600 font-medium">Retreat:</span>
                  <span className="font-semibold text-gray-800">{campName || 'N/A'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600 font-medium">Attendees:</span>
                  <span className="font-semibold text-gray-800">{attendees.length || 0} {attendees.length === 1 ? 'person' : 'people'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600 font-medium">Sessions:</span>
                  <span className="font-semibold text-gray-800">{sessions[0]?.visits || sessions.length || 0} {(sessions[0]?.visits || sessions.length) === 1 ? 'day' : 'days'}</span>
                </div>
                <div className="flex justify-between items-center pt-2 border-t border-blue-300">
                  <span className="text-gray-600 font-semibold">Registration Fee:</span>
                  <span className="text-lg font-bold text-green-600">FREE</span>
                </div>
              </div>
            </div>

            {/* Email Confirmation */}
            {emailSent ? (
              <div className="bg-green-50 border border-green-200 rounded-xl p-4">
                <div className="flex items-center gap-3">
                  <i className="fas fa-envelope text-lg text-green-600"></i>
                  <div className="flex-1">
                    <p className="text-sm font-semibold text-green-900 mb-1">Confirmation Email Sent</p>
                    <p className="text-sm text-green-700">
                      A confirmation email has been sent to <strong>{email || 'your email'}</strong>
                    </p>
                  </div>
                </div>
              </div>
            ) : (
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                <div className="flex items-center gap-3">
                  <i className="fas fa-triangle-exclamation text-lg text-amber-600"></i>
                  <div className="flex-1">
                    <p className="text-sm font-semibold text-amber-900 mb-1">Email Not Sent</p>
                    <p className="text-sm text-amber-800">
                      We could not send the confirmation email to <strong>{email || 'your email'}</strong>.
                    </p>
                    {emailError ? (
                      <p className="mt-2 text-xs text-amber-800 bg-amber-100/60 border border-amber-200 rounded-lg px-3 py-2 break-words">
                        {emailError}
                      </p>
                    ) : null}
                  </div>
                </div>
              </div>
            )}

            {/* Invoice Actions */}
            {receiptUrl && (
              <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-2xl p-4 border-2 border-purple-200">
                <div className="flex items-center gap-2 mb-2">
                  <i className="fas fa-file-invoice text-2xl text-purple-600"></i>
                  <h3 className="font-bold text-gray-800 text-lg">Registration Receipt</h3>
                </div>
                <div className="flex gap-3">
                  <button
                    onClick={handleViewInvoice}
                    className="flex-1 bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-6 rounded-xl transition-all duration-300 shadow-md hover:shadow-lg flex items-center justify-center gap-2"
                  >
                    <i className="fas fa-eye"></i>
                    View Receipt
                  </button>
                  <button
                    onClick={handleDownloadInvoice}
                    className="flex-1 bg-green-600 hover:bg-green-700 text-white font-semibold py-3 px-6 rounded-xl transition-all duration-300 shadow-md hover:shadow-lg flex items-center justify-center gap-2"
                  >
                    <i className="fas fa-download"></i>
                    Download
                  </button>
                </div>
              </div>
            )}

            {/* Attendees List */}
            {attendees && attendees.length > 0 && (
              <div className="bg-white border-2 border-gray-200 rounded-2xl p-6 hidden">
                <h3 className="font-bold text-gray-800 text-lg mb-4 flex items-center gap-2">
                  <i className="fas fa-users text-purple-600"></i>
                  Registered Attendees
                </h3>
                <div className="space-y-2">
                  {attendees.map((attendee, index) => (
                    <div key={index} className="flex items-center gap-3 bg-purple-50 px-4 py-3 rounded-xl">
                      <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-pink-500 rounded-full flex items-center justify-center text-white font-bold">
                        {(attendee.firstName || attendee.Student_First_Name)?.charAt(0) || 'A'}
                      </div>
                      <div className="flex-1">
                        <p className="font-semibold text-gray-900">
                          {attendee.firstName || attendee.Student_First_Name} {attendee.lastName || attendee.Student_Last_Name}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="p-4 bg-gray-50 border-t border-gray-200">
            <button
              onClick={handleReturnHome}
              className="w-full bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white font-semibold py-3 px-4 rounded-xl transition-all duration-300 shadow-md hover:shadow-lg flex items-center justify-center gap-2"
            >
              <i className="fas fa-home"></i>
              Return to Home
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
